import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  IconPlus,
  IconToggleRight,
  IconToggleLeft,
  IconUnlink,
  IconX,
  IconMinus,
} from '@tabler/icons-react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { BASE_NODE_STYLE } from '../constants';
import { parseRatingConfig } from '../utils';
import type { QuestionNodeData } from '../types';

const getBorderConfig = (selected: boolean) => ({
  node: {
    width: '1px',
    style: 'solid',
    color: selected ? '#1976d2' : (BASE_NODE_STYLE.borderColor || '#1a192b'),
  },
  textarea: {
    width: '2px',
    style: 'solid',
    color: '#1976d2',
  },
  box: {
    width: '1px',
    style: 'solid',
    color: '#ccc',
  },
  textField: {
    width: '1px',
    style: 'solid',
    color: '#1976d2',
  },
});

export const QuestionNode = React.memo(({ data, selected, id }: NodeProps<QuestionNodeData>) => {
  const nodeData = data as QuestionNodeData;
  
  const BORDER_CONFIG = getBorderConfig(selected);
  
  const borderWidth = parseInt(BORDER_CONFIG.node.width) || 1;
  const nodeWidth = BASE_NODE_STYLE.width || 280;
  const contentWidth = nodeWidth - (borderWidth * 2);
  const greenBorderWidth = 3;
  const outerContainerWidth = nodeWidth - (greenBorderWidth * 2);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(nodeData.label || '');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [selectedOptionType, setSelectedOptionType] = useState<'options' | 'choices'>('options');
  const originalButtonRef = useRef<HTMLButtonElement | null>(null);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [typeMenuPosition, setTypeMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const typeMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [typeMenuDragOffset, setTypeMenuDragOffset] = useState<{ x: number; y: number } | null>(null);
  
  const [optionMenuOpen, setOptionMenuOpen] = useState(false);
  const [optionMenuPosition, setOptionMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const optionMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [optionMenuDragOffset, setOptionMenuDragOffset] = useState<{ x: number; y: number } | null>(null);
  
  const [ratingMenuOpen, setRatingMenuOpen] = useState(false);
  const [ratingMenuPosition, setRatingMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [tempRateAmount, setTempRateAmount] = useState<number>(5);
  const [tempFormat, setTempFormat] = useState<'numerical' | 'satisfaction'>('numerical');
  const ratingMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [ratingMenuDragOffset, setRatingMenuDragOffset] = useState<{ x: number; y: number } | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const optionInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const onUpdate = nodeData.onUpdate;
  const onAddOption = nodeData.onAddOption;
  const onToggleConditionalMode = nodeData.onToggleConditionalMode;
  const onRemoveOption = nodeData.onRemoveOption;
  const onUpdateRatingConfig = nodeData.onUpdateRatingConfig;
  const onAddRatingScale = nodeData.onAddRatingScale;
  const onToggleOptional = nodeData.onToggleOptional;
  const onUnlinkNode = nodeData.onUnlinkNode;
  const onReconnectAllOrphanedSubQuestions = nodeData.onReconnectAllOrphanedSubQuestions;
  const isMultiSelection = nodeData.isMultiSelection === true;
  
  const { getNodes } = useReactFlow();
  
  const hasSubQuestions = useMemo(() => {
    if (id.match(/^\d+\.\d+$/)) {
      return false;
    }
    const allNodes = getNodes();
    return allNodes.some(node => {
      const nodeId = node.id;
      const subQuestionPattern = new RegExp(`^${id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.\\d+$`);
      return subQuestionPattern.test(nodeId);
    });
  }, [id, getNodes]);
  
  const createConditionalQuestions = useMemo(() => {
    return nodeData.createConditionalQuestions === true;
  }, [nodeData.createConditionalQuestions]);

  const canEdit = id !== 'start' && id !== 'end';

  const { existingOptions, isChoices } = useMemo(() => {
    const label = nodeData.label || '';
    const optionLine = label.split('\n').find((line: string) => 
      line.trim().toLowerCase().startsWith('options:') || 
      line.trim().toLowerCase().startsWith('choices:')
    );
    if (!optionLine) return { existingOptions: [], isChoices: false };
    const [, list] = optionLine.split(':');
    if (!list) return { existingOptions: [], isChoices: false };
    const options = list.split(',').map((item: string) => item.trim()).filter(Boolean);
    const isChoices = optionLine.trim().toLowerCase().startsWith('choices:');
    return { existingOptions: options, isChoices };
  }, [nodeData.label]);
  
  const questionType = useMemo(() => {
    if (nodeData.questionType === 'options' || nodeData.questionType === 'choices') {
      return nodeData.questionType;
    }
    if (existingOptions.length > 0) {
      return isChoices ? 'choices' : 'options';
    }
    return undefined;
  }, [nodeData.questionType, existingOptions.length, isChoices]);

  const isOptional = useMemo(() => {
    const label = nodeData.label || '';
    return label.toLowerCase().includes('[optional]') || (nodeData as { optional?: boolean }).optional === true;
  }, [nodeData]);

  const isRatingQuestion = useMemo(() => {
    const label = nodeData.label || '';
    return label.toLowerCase().includes('scale:');
  }, [nodeData.label]);

  const ratingConfig = useMemo(() => {
    if (!isRatingQuestion) return null;
    return parseRatingConfig(nodeData.label || '') || { format: 'numerical' as const, rate_amount: 5 };
  }, [nodeData.label, isRatingQuestion]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (anchorEl && optionInputRef.current) {
      setTimeout(() => {
        if (optionInputRef.current) {
          optionInputRef.current.focus();
          if (optionInputRef.current instanceof HTMLInputElement || optionInputRef.current instanceof HTMLTextAreaElement) {
            optionInputRef.current.select();
          }
        }
      }, 200);
    }
  }, [anchorEl, selectedOptionType]);

  useEffect(() => {
    if (optionMenuOpen && optionInputRef.current) {
      setTimeout(() => {
        if (optionInputRef.current) {
          optionInputRef.current.focus();
          if (optionInputRef.current instanceof HTMLInputElement || optionInputRef.current instanceof HTMLTextAreaElement) {
            optionInputRef.current.select();
          }
        }
      }, 100);
    }
  }, [optionMenuOpen]);

  const handleCloseOptionMenu = () => {
    setOptionMenuOpen(false);
    setOptionMenuPosition(null);
    setNewOptionValue('');
    optionMenuButtonRef.current = null;
    setOptionMenuDragOffset(null);
  };

  const handleOptionMenuMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    
    if (!optionMenuPosition) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = optionMenuPosition.left;
    const startTop = optionMenuPosition.top;
    
    const offsetX = startX - startLeft;
    const offsetY = startY - startTop;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.stopPropagation();
      const newLeft = moveEvent.clientX - offsetX;
      const newTop = moveEvent.clientY - offsetY;
      setOptionMenuPosition({
        left: newLeft,
        top: newTop,
      });
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.stopPropagation();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setOptionMenuDragOffset(null);
    };

    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);
  };

  useEffect(() => {
    if (!optionMenuOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      const menuElement = document.querySelector('[data-option-menu]');
      if (menuElement?.contains(target)) {
        return;
      }
      
      if (optionMenuButtonRef.current?.contains(target)) {
        return;
      }
      
      handleCloseOptionMenu();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [optionMenuOpen]);

  useEffect(() => {
    if (!typeMenuOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      const menuElement = document.querySelector('[data-type-menu]');
      if (menuElement?.contains(target)) {
        return;
      }
      
      if (typeMenuButtonRef.current?.contains(target)) {
        handleCloseTypeMenu();
        return;
      }
      
      handleCloseTypeMenu();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [typeMenuOpen]);

  useEffect(() => {
    if (!ratingMenuOpen) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      const menuElement = document.querySelector('[data-rating-menu]');
      if (menuElement?.contains(target) || ratingMenuButtonRef.current?.contains(target)) {
        return;
      }
      
      handleCloseRatingMenu();
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
    }, 200);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [ratingMenuOpen]);

  const handleDoubleClick = () => {
    if (canEdit) {
      setIsEditing(true);
      setEditValue(nodeData.label || '');
    }
  };

  const handleSave = () => {
    if (onUpdate && editValue.trim() !== '') {
      onUpdate(id, editValue.trim());
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(nodeData.label || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const valueWithoutNewlines = e.target.value.replace(/\n/g, ' ');
    setEditValue(valueWithoutNewlines);
  };

  const handleRemoveOption = (option: string) => {
    console.log('🔴 [X BUTTON PATH] QuestionNode handleRemoveOption called:', { 
      nodeId: id, 
      option, 
      hasCallback: !!onRemoveOption,
      timestamp: new Date().toISOString()
    });
    if (onRemoveOption) {
      console.log('🔴 [X BUTTON PATH] Calling onRemoveOption callback:', { nodeId: id, option });
      onRemoveOption(id, option);
    } else {
      console.warn('⚠️ [X BUTTON PATH] QuestionNode: onRemoveOption callback not provided');
    }
  };

  const handleAddButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    
    const button = event.currentTarget;
    typeMenuButtonRef.current = button;
    
    const currentQuestionType = questionType;
    
    if (currentQuestionType && (currentQuestionType === 'options' || currentQuestionType === 'choices') && existingOptions.length > 0) {
      setSelectedOptionType(currentQuestionType);
      optionMenuButtonRef.current = button;
      const popoverWidth = 250;
      setOptionMenuPosition({
        top: window.scrollY,
        left: window.scrollX + popoverWidth/3,
      });
      setOptionMenuOpen(true);
      setNewOptionValue('');
      return;
    }
    
    const menuWidth = 180;
    const topPos = window.scrollY;
    const leftPos = window.scrollX + menuWidth/3;
    
    setTypeMenuPosition({
      top: topPos,
      left: leftPos,
    });
    setTypeMenuOpen(true);
  };

  const handleCloseTypeMenu = () => {
    setTypeMenuOpen(false);
    setTypeMenuPosition(null);
    typeMenuButtonRef.current = null;
    setTypeMenuDragOffset(null);
  };

  const handleTypeMenuMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    
    if (!typeMenuPosition) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = typeMenuPosition.left;
    const startTop = typeMenuPosition.top;
    
    const offsetX = startX - startLeft;
    const offsetY = startY - startTop;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.stopPropagation();
      const newLeft = moveEvent.clientX - offsetX;
      const newTop = moveEvent.clientY - offsetY;
      setTypeMenuPosition({
        left: newLeft,
        top: newTop,
      });
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.stopPropagation();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setTypeMenuDragOffset(null);
    };

    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);
  };

  const handleTypeSelection = (type: 'options' | 'choices' | 'rating') => {
    const currentPosition = typeMenuPosition;
    
    handleCloseTypeMenu();
    
    setTimeout(() => {
      if (type === 'options' || type === 'choices') {
        setSelectedOptionType(type);
        optionMenuButtonRef.current = typeMenuButtonRef.current;
        if (currentPosition) {
          setOptionMenuPosition({
            top: currentPosition.top,
            left: currentPosition.left,
          });
        } else {
          const popoverWidth = 250;
          setOptionMenuPosition({
            top: window.scrollY,
            left: window.scrollX + popoverWidth/3,
          });
        }
        setOptionMenuOpen(true);
        setNewOptionValue('');
      } else if (type === 'rating') {
        ratingMenuButtonRef.current = typeMenuButtonRef.current;
        if (currentPosition) {
          setRatingMenuPosition({
            top: currentPosition.top,
            left: currentPosition.left,
          });
        } else {
          const menuWidth = 300;
          setRatingMenuPosition({
            top: window.scrollY,
            left: window.scrollX + menuWidth/3,
          });
        }
        setTempRateAmount(ratingConfig?.rate_amount || 5);
        setTempFormat(ratingConfig?.format || 'numerical');
        setRatingMenuOpen(true);
      }
    }, 100);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setNewOptionValue('');
    originalButtonRef.current = null;
  };


  const handleAddOption = () => {
    if (newOptionValue.trim() && onAddOption) {
      const isSubQuestion = id.match(/^\d+\.\d+$/);
      const shouldCreateConditional = (questionType === 'choices' || isSubQuestion) ? false : createConditionalQuestions;
      onAddOption(id, newOptionValue.trim(), selectedOptionType, shouldCreateConditional);
      handleCloseMenu();
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseMenu();
    }
  };

  const handleRatingSettingsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const button = event.currentTarget;
    ratingMenuButtonRef.current = button;
    const menuWidth = 300;
    setRatingMenuPosition({
      top: window.scrollY,
      left: window.scrollX + menuWidth/3,
    });
    setTempRateAmount(ratingConfig?.rate_amount || 5);
    setTempFormat(ratingConfig?.format || 'numerical');
    setRatingMenuOpen(true);
  };

  const handleCloseRatingMenu = () => {
    setRatingMenuOpen(false);
    setRatingMenuPosition(null);
    ratingMenuButtonRef.current = null;
    setRatingMenuDragOffset(null);
    setTempRateAmount(5);
    setTempFormat('numerical');
  };

  const handleCreateRatingScale = () => {
    const finalRateAmount = tempFormat === 'satisfaction' ? 5 : tempRateAmount;
    handleRatingConfigUpdate(tempFormat, finalRateAmount);
    handleCloseRatingMenu();
  };

  const handleRatingMenuMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    
    if (!ratingMenuPosition) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = ratingMenuPosition.left;
    const startTop = ratingMenuPosition.top;
    
    const offsetX = startX - startLeft;
    const offsetY = startY - startTop;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.stopPropagation();
      const newLeft = moveEvent.clientX - offsetX;
      const newTop = moveEvent.clientY - offsetY;
      setRatingMenuPosition({
        left: newLeft,
        top: newTop,
      });
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.stopPropagation();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setRatingMenuDragOffset(null);
    };

    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('mouseup', handleMouseUp, true);
  };

  const handleRatingConfigUpdate = (format: 'numerical' | 'satisfaction', rateAmount: number) => {
    if (onUpdateRatingConfig) {
      onUpdateRatingConfig(id, format, rateAmount);
    }
  };

  const open = Boolean(anchorEl);

  const nodeStyle = {
    ...BASE_NODE_STYLE,
    position: 'relative' as const,
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
    overflow: 'visible' as const,
    width: '100%',
    minWidth: 'auto',
    padding: 0,
  };
  
  return (
    <div style={{ 
      position: 'relative',
      height: '100%',
      overflow: 'visible',
      borderRadius: BASE_NODE_STYLE.borderRadius || 8,
      backgroundColor: BASE_NODE_STYLE.backgroundColor || '#fff',
      boxSizing: 'border-box',
      padding: BASE_NODE_STYLE.padding || 12,
    }}>
      <div style={nodeStyle} onDoubleClick={handleDoubleClick}>
        {/* Top: input only */}
        <Handle 
          type="target" 
          position={Position.Top} 
          id="top-input" 
          style={{ 
            position: 'absolute',
            top: '-18.0px',
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: selected ? 1 : 0,
            visibility: selected ? 'visible' : 'hidden',
          }} 
        />
        {/* Left: input/output (bidirectional) */}
        <Handle 
          type="target" 
          position={Position.Left} 
          id="left-input" 
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '-18.0px',
            transform: 'translateY(-50%)',
            opacity: selected ? 1 : 0,
            visibility: selected ? 'visible' : 'hidden',
          }} 
        />
        <Handle 
          type="source" 
          position={Position.Left} 
          id="left-output" 
          style={{ 
            position: 'absolute',
            top: '50%',
            left: '-18.0px',
            transform: 'translateY(-50%)',
            opacity: selected ? 1 : 0,
            visibility: selected ? 'visible' : 'hidden',
          }} 
        />
        {/* Right: input/output (bidirectional) */}
        <Handle 
          type="target" 
          position={Position.Right} 
          id="right-input" 
          style={{ 
            position: 'absolute',
            top: '50%',
            right: '-18.0px',
            transform: 'translateY(-50%)',
            opacity: selected ? 1 : 0,
            visibility: selected ? 'visible' : 'hidden',
          }} 
        />
        <Handle 
          type="source" 
          position={Position.Right} 
          id="right-output" 
          style={{ 
            position: 'absolute',
            top: '50%',
            right: '-18.0px',
            transform: 'translateY(-50%)',
            opacity: selected ? 1 : 0,
            visibility: selected ? 'visible' : 'hidden',
          }} 
        />
        {/* Bottom: output only (default for auto-linking) */}
        <Handle 
          type="source" 
          position={Position.Bottom} 
          id="bottom-output" 
          style={{ 
            position: 'absolute',
            bottom: '-18.0px',
            left: '50%',
            transform: 'translateX(-50%)',
            opacity: selected ? 1 : 0,
            visibility: selected ? 'visible' : 'hidden',
          }} 
        />
        
        {/* Add Option Button */}
        {canEdit && (selected || isEditing) && !isRatingQuestion && !isMultiSelection && (
          <button
            onClick={handleAddButtonClick}
            className="absolute top-1 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border-none bg-[#1976d2] text-white hover:bg-[#1565c0]"
          >
            <IconPlus className="size-3.5" />
          </button>
        )}
        
        {/* Rating Settings Button */}
        {canEdit && (selected || isEditing) && isRatingQuestion && !isMultiSelection && (
          <button
            onClick={handleRatingSettingsClick}
            className="absolute top-1 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border-none bg-[#2196f3] text-white hover:bg-[#1976d2]"
          >
            <IconPlus className="size-3.5" />
          </button>
        )}
        
        {/* Unlink Button */}
        {canEdit && selected && !!onUnlinkNode && !isMultiSelection && !id.match(/^\d+\.\d+$/) && !hasSubQuestions && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (onUnlinkNode) {
                onUnlinkNode(id);
              }
            }}
            className="absolute top-8 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border-none bg-[#f44336] text-white hover:bg-[#d32f2f]"
            title="Unlink this question from all connections"
          >
            <IconUnlink className="size-3.5" />
          </button>
        )}

        {isEditing ? (
          <textarea
            ref={inputRef}
            value={editValue}
            onChange={handleInputChange}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text/plain').replace(/\n/g, ' ');
              const newValue = editValue.substring(0, (e.currentTarget.selectionStart || 0)) + 
                             pastedText + 
                             editValue.substring(e.currentTarget.selectionEnd || 0);
              setEditValue(newValue);
            }}
            style={{
              width: 'calc(100% - 40px)',
              minHeight: '60px',
              border: `${BORDER_CONFIG.textarea.width} ${BORDER_CONFIG.textarea.style} ${BORDER_CONFIG.textarea.color}`,
              outline: 'none',
              padding: '4px',
              fontSize: '12px',
              backgroundColor: '#fff',
              resize: 'vertical',
              fontFamily: 'inherit',
              cursor: 'text',
              boxSizing: 'border-box',
              margin: '0 20px',
              borderRadius: '4px',
            }}
          />
        ) : (
          <div style={{ cursor: 'pointer' }}>
            {nodeData.label}
            {existingOptions.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-0.5 rounded p-1">
                {existingOptions.map((opt: string, idx: number) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-0.5 rounded-full px-2 text-white"
                    style={{
                      fontSize: '10px',
                      height: '20px',
                      backgroundColor: isChoices ? '#4caf50' : '#2196f3',
                    }}
                  >
                    {opt}
                    {canEdit && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOption(opt);
                        }}
                        className="ml-0.5 inline-flex items-center justify-center rounded-full border-none bg-transparent p-0 text-white hover:text-red-100"
                        style={{ cursor: 'pointer', lineHeight: 1 }}
                      >
                        <IconX style={{ width: 14, height: 14, marginRight: 2 }} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {/* Optional/Required Toggle */}
            {canEdit && (selected || isEditing) && (
              <div className="mt-1 flex items-center gap-1 rounded p-1">
                <span className="text-[10px]">
                  {isOptional ? 'Optional' : 'Required'}
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleOptional) {
                      onToggleOptional(id, !isOptional);
                    }
                  }}
                  className="flex h-5 w-5 items-center justify-center border-none bg-transparent p-0"
                  style={{ cursor: 'pointer' }}
                >
                  {isOptional ? (
                    <IconToggleLeft className="text-[#9e9e9e]" style={{ width: 20, height: 20 }} />
                  ) : (
                    <IconToggleRight className="text-[#4caf50]" style={{ width: 20, height: 20 }} />
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Type Selection Menu - First menu when clicking + */}
      {typeMenuOpen && typeMenuPosition && (
        <div
          data-type-menu
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          className="rounded border border-[#ccc] bg-white p-1 shadow-lg"
          style={{
            position: 'fixed',
            top: `${typeMenuPosition.top}px`,
            left: `${typeMenuPosition.left}px`,
            zIndex: 1300,
            minWidth: 180,
            visibility: 'visible',
            opacity: 1,
          }}
        >
          {/* Header with title and close button - draggable */}
          <div 
            className="mb-1 flex cursor-move items-center justify-between select-none px-1 pt-1"
            onMouseDown={handleTypeMenuMouseDown}
          >
            <span className="text-sm font-medium">
              Add Configuration
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleCloseTypeMenu}
            >
              <IconX className="size-3.5" />
            </Button>
          </div>
          <div
            onClick={() => handleTypeSelection('options')} 
            className="cursor-pointer rounded px-3 py-2 text-sm hover:bg-black/[0.04]"
          >
            Radio
          </div>
          <div
            onClick={() => handleTypeSelection('choices')} 
            className="cursor-pointer rounded px-3 py-2 text-sm hover:bg-black/[0.04]"
          >
            Checkbox
          </div>
          <div
            onClick={() => handleTypeSelection('rating')} 
            className="cursor-pointer rounded px-3 py-2 text-sm hover:bg-black/[0.04]"
          >
            Rating Scale
          </div>
        </div>
      )}
      {/* Option Input Menu - For Radio/Checkbox */}
      {optionMenuOpen && optionMenuPosition && (
        <div
          data-option-menu
          onClick={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          className="rounded border border-[#ccc] bg-white p-4 shadow-lg"
          style={{
            position: 'fixed',
            top: `${optionMenuPosition.top}px`,
            left: `${optionMenuPosition.left}px`,
            zIndex: 1300,
            minWidth: 250,
            maxWidth: 300,
          }}
        >
          {/* Header with title and close button - draggable */}
          <div 
            className="mb-1 flex cursor-move items-center justify-between select-none"
            onMouseDown={handleOptionMenuMouseDown}
          >
            <span className="text-sm font-medium">
              Add {selectedOptionType === 'choices' ? 'Checkbox Choice' : 'Radio Option'}
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleCloseOptionMenu}
            >
              <IconX className="size-3.5" />
            </Button>
          </div>
          <Input
            ref={optionInputRef as React.Ref<HTMLInputElement>}
            className="my-1 border-2 border-[#1976d2] hover:border-[#1565c0] focus-visible:border-[#1976d2] focus-visible:ring-0"
            placeholder="e.g., Yes, No"
            value={newOptionValue}
            onChange={(e) => {
              setNewOptionValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddOption();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCloseOptionMenu();
              }
            }}
            autoFocus={true}
            onFocus={(e) => {
              (e.target as HTMLInputElement).select();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          />
          {existingOptions.length > 0 && (
            <div 
              className="mb-1 rounded p-1"
              style={{ border: `${BORDER_CONFIG.box.width} ${BORDER_CONFIG.box.style} ${BORDER_CONFIG.box.color}` }}
            >
              <span className="text-xs text-muted-foreground">
                Existing options:
              </span>
              <div className="mt-0.5 flex flex-wrap gap-0.5 rounded p-0.5">
                {existingOptions.map((opt: string, idx: number) => (
                  <span
                    key={idx}
                    className="inline-flex items-center rounded-full border border-[#ccc] bg-gray-100 px-2"
                    style={{ fontSize: '10px', height: '20px' }}
                  >
                    {opt}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Conditional checkbox - only for radio questions, not checkbox or sub-questions */}
          {questionType !== 'choices' && selectedOptionType !== 'choices' && !id.match(/^\d+\.\d+$/) && (
            <label className="flex items-center gap-2 py-1">
              <Checkbox
                checked={createConditionalQuestions}
                onCheckedChange={(checked) => {
                  if (onToggleConditionalMode) {
                    onToggleConditionalMode(id, checked as boolean);
                  }
                }}
              />
              <span className="text-[11px]">
                Create conditional questions for all options
              </span>
            </label>
          )}
          <div className="mt-2 flex justify-end gap-1 rounded p-1">
            <Button 
              size="sm" 
              onClick={handleAddOption}
              disabled={!newOptionValue.trim()}
            >
              Add
            </Button>
          </div>
        </div>
      )}
      {/* Rating Configuration Popover */}
      {ratingMenuOpen && ratingMenuPosition && (
        <div
          data-rating-menu
          onClick={(e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('input, button, label, select')) {
              return;
            }
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest('input, button, label, select')) {
              return;
            }
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          className="rounded border border-[#ccc] bg-white p-4 shadow-lg"
          style={{
            position: 'fixed',
            top: `${ratingMenuPosition.top}px`,
            left: `${ratingMenuPosition.left}px`,
            zIndex: 1300,
            minWidth: 300,
          }}
        >
          {/* Header with title and close button - draggable */}
          <div 
            className="mb-2 flex cursor-move items-center justify-between select-none"
            onMouseDown={handleRatingMenuMouseDown}
          >
            <span className="text-sm font-medium">
              Rating Configuration
            </span>
            <Button
              variant="ghost"
              size="icon-xs"
              onClick={handleCloseRatingMenu}
            >
              <IconX className="size-3.5" />
            </Button>
          </div>
          <div className="mt-1 mb-2">
            <span className="mb-1 block text-xs">
              Format
            </span>
            <div
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.stopPropagation();
                const newFormat = e.target.value as 'numerical' | 'satisfaction';
                setTempFormat(newFormat);
                if (newFormat === 'satisfaction') {
                  setTempRateAmount(5);
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <label
                className="flex items-center gap-2 py-1"
                onClick={(e) => { e.stopPropagation(); }}
                onMouseDown={(e) => { e.stopPropagation(); }}
              >
                <input
                  type="radio"
                  name={`rating-format-${id}`}
                  value="numerical"
                  checked={tempFormat === 'numerical'}
                  onChange={() => {}}
                  onClick={(e) => { e.stopPropagation(); }}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  className="size-4 accent-primary"
                />
                <span className="text-sm">Numerical</span>
              </label>
              <label
                className="flex items-center gap-2 py-1"
                onClick={(e) => { e.stopPropagation(); }}
                onMouseDown={(e) => { e.stopPropagation(); }}
              >
                <input
                  type="radio"
                  name={`rating-format-${id}`}
                  value="satisfaction"
                  checked={tempFormat === 'satisfaction'}
                  onChange={() => {}}
                  onClick={(e) => { e.stopPropagation(); }}
                  onMouseDown={(e) => { e.stopPropagation(); }}
                  className="size-4 accent-primary"
                />
                <span className="text-sm">Satisfaction</span>
              </label>
            </div>
          </div>
          <div className="mt-1 mb-2">
            <span className="mb-0.5 block text-xs">
              Rate Amount
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  if (tempFormat === 'satisfaction') {
                    return;
                  }
                  if (tempRateAmount > 1) {
                    setTempRateAmount(tempRateAmount - 1);
                  }
                }}
                disabled={tempRateAmount <= 1 || tempFormat === 'satisfaction'}
              >
                <IconMinus className="size-3" />
              </Button>
              <input
                type="number"
                value={tempRateAmount}
                disabled={tempFormat === 'satisfaction'}
                onChange={(e) => {
                  e.stopPropagation();
                  if (tempFormat === 'satisfaction') {
                    return;
                  }
                  const value = e.target.value;
                  if (value === '' || value === '-') {
                    return;
                  }
                  const rateAmount = parseInt(value, 10);
                  if (!isNaN(rateAmount) && rateAmount >= 1 && rateAmount <= 10) {
                    setTempRateAmount(rateAmount);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onBlur={(e) => {
                  if (tempFormat === 'satisfaction') {
                    setTempRateAmount(5);
                    return;
                  }
                  const value = e.target.value;
                  const rateAmount = parseInt(value, 10);
                  if (isNaN(rateAmount) || rateAmount < 1 || rateAmount > 10) {
                    setTempRateAmount(5);
                  }
                }}
                min={1}
                max={10}
                step={1}
                className="h-8 w-20 rounded-lg border-2 border-[#1976d2] bg-transparent text-center text-sm outline-none hover:border-[#1565c0] focus:border-[#1976d2] disabled:opacity-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              <Button
                variant="outline"
                size="icon-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  if (tempFormat === 'satisfaction') {
                    return;
                  }
                  if (tempRateAmount < 10) {
                    setTempRateAmount(tempRateAmount + 1);
                  }
                }}
                disabled={tempRateAmount >= 10 || tempFormat === 'satisfaction'}
              >
                <IconPlus className="size-3" />
              </Button>
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-1">
            <Button size="sm" onClick={handleCreateRatingScale}>
              Create
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});

QuestionNode.displayName = 'QuestionNode';
